import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

// Base selectors with shallow equality for better performance
const selectInvoiceState = (state: RootState) => state.invoices;
const selectAuthState = (state: RootState) => state.auth;

// Optimized equality check for arrays - Safari performance optimized
const arrayEqualityCheck = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  
  // For Safari performance, limit deep comparison to first 50 items
  const maxItems = Math.min(a.length, 50);
  for (let i = 0; i < maxItems; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Memoized selectors for better performance with Safari optimizations
export const selectInvoices = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.invoices,
  {
    memoizeOptions: {
      resultEqualityCheck: arrayEqualityCheck
    }
  }
);

export const selectInvoicesLoading = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.loading
);

export const selectInvoicesError = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.error
);

export const selectInvoicesPagination = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.pagination
);

export const selectCurrentInvoice = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.currentInvoice
);

export const selectUser = createSelector(
  [selectAuthState],
  (authState) => authState.user
);

export const selectIsAuthenticated = createSelector(
  [selectAuthState],
  (authState) => authState.isAuthenticated
);

// Complex selectors with filtering
export const selectInvoicesByStatus = createSelector(
  [selectInvoices, (state: RootState, status: string) => status],
  (invoices, status) => {
    if (status === 'all') return invoices;
    return invoices.filter(invoice => invoice.status === status);
  }
);

export const selectInvoicesByBusinessUnit = createSelector(
  [selectInvoices, (state: RootState, businessUnitId: string) => businessUnitId],
  (invoices, businessUnitId) => {
    if (businessUnitId === 'all') return invoices;
    return invoices.filter(invoice => invoice.businessUnitId?.toString() === businessUnitId);
  }
);

export const selectInvoicesByDateRange = createSelector(
  [selectInvoices, (state: RootState, dateFrom: Date | null, dateTo: Date | null) => ({ dateFrom, dateTo })],
  (invoices, { dateFrom, dateTo }) => {
    if (!dateFrom && !dateTo) return invoices;
    
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.invoiceDate);
      
      if (dateFrom && invoiceDate < dateFrom) return false;
      if (dateTo && invoiceDate > dateTo) return false;
      
      return true;
    });
  }
);

export const selectInvoicesBySearchTerm = createSelector(
  [selectInvoices, (state: RootState, searchTerm: string) => searchTerm],
  (invoices, searchTerm) => {
    if (!searchTerm.trim()) return invoices;
    
    const term = searchTerm.toLowerCase();
    return invoices.filter(invoice => 
      invoice.invoiceNumber.toLowerCase().includes(term) ||
      invoice.supplierName.toLowerCase().includes(term) ||
      invoice.customerName.toLowerCase().includes(term) ||
      invoice.supplierICO?.toLowerCase().includes(term) ||
      invoice.customerICO?.toLowerCase().includes(term)
    );
  }
);

// Safari-optimized filter selector with early returns and efficient filtering
export const selectFilteredInvoices = createSelector(
  [
    selectInvoices,
    (state: RootState, filters: {
      status: string;
      businessUnit: string;
      dateFrom: Date | null;
      dateTo: Date | null;
      searchTerm: string;
    }) => filters
  ],
  (invoices, filters) => {
    // Early return if no filters applied - Safari performance optimization
    if (filters.status === 'all' && 
        filters.businessUnit === 'all' && 
        !filters.dateFrom && 
        !filters.dateTo && 
        !filters.searchTerm.trim()) {
      return invoices;
    }
    
    // Safari optimization: limit processing for very large datasets
    const maxProcessItems = 1000;
    const itemsToProcess = invoices.length > maxProcessItems ? invoices.slice(0, maxProcessItems) : invoices;
    
    let filtered = itemsToProcess;
    
    // Apply status filter (most selective first for Safari performance)
    if (filters.status !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filters.status);
    }
    
    // Apply search term filter (expensive operation, do after status filter)
    if (filters.searchTerm.trim()) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber.toLowerCase().includes(term) ||
        invoice.supplierName.toLowerCase().includes(term) ||
        invoice.customerName.toLowerCase().includes(term) ||
        (invoice.supplierICO && invoice.supplierICO.toLowerCase().includes(term)) ||
        (invoice.customerICO && invoice.customerICO.toLowerCase().includes(term))
      );
    }
    
    // Apply business unit filter
    if (filters.businessUnit !== 'all') {
      filtered = filtered.filter(invoice => 
        invoice.businessUnitId?.toString() === filters.businessUnit
      );
    }
    
    // Apply date range filter (expensive Date operations, do last)
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter(invoice => {
        try {
          const invoiceDate = new Date(invoice.invoiceDate);
          if (isNaN(invoiceDate.getTime())) return false;
          
          if (filters.dateFrom && invoiceDate < filters.dateFrom) return false;
          if (filters.dateTo && invoiceDate > filters.dateTo) return false;
          
          return true;
        } catch {
          return false;
        }
      });
    }
    
    return filtered;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: arrayEqualityCheck
    }
  }
);

// Statistics selectors
export const selectInvoiceStats = createSelector(
  [selectInvoices],
  (invoices) => {
    const stats = {
      total: invoices.length,
      byStatus: {} as Record<string, number>,
      totalAmount: 0,
      averageAmount: 0,
    };
    
    invoices.forEach(invoice => {
      // Count by status
      stats.byStatus[invoice.status] = (stats.byStatus[invoice.status] || 0) + 1;
      
      // Sum total amount
      stats.totalAmount += invoice.totalAmount;
    });
    
    // Calculate average
    stats.averageAmount = stats.total > 0 ? stats.totalAmount / stats.total : 0;
    
    return stats;
  }
);

// Universal performance-optimized selector (works for all browsers including Safari)
export const selectOptimizedInvoices = createSelector(
  [selectInvoices, (state: RootState, pageSize: number) => pageSize],
  (invoices, pageSize = 50) => {
    // Limit processing for performance across all browsers
    // Safari benefits most from this optimization
    const maxItems = Math.min(pageSize * 2, 100); // Show 2 pages worth, max 100 items
    return invoices.slice(0, maxItems);
  }
);

// Legacy selector for backward compatibility
export const selectSafariOptimizedInvoices = selectOptimizedInvoices;

// Memoized invoice by ID selector
export const selectInvoiceById = createSelector(
  [selectInvoices, (state: RootState, id: number) => id],
  (invoices, id) => invoices.find(invoice => invoice.id === id)
);

// Selector for invoice actions (view, edit, delete permissions)
export const selectInvoiceActions = createSelector(
  [selectUser],
  (user) => ({
    canView: true, // All authenticated users can view
    canEdit: user?.role === 'admin' || user?.role === 'superadmin',
    canDelete: user?.role === 'admin' || user?.role === 'superadmin',
    canBulkEdit: user?.role === 'admin' || user?.role === 'superadmin',
    canBulkDelete: user?.role === 'admin' || user?.role === 'superadmin',
  })
);
